import type { V1PlanInfo } from "schemas/billing";
import type { BillingInterval } from "schemas/generated/billing";
import type { BillingUpgradeDowngradeModalProps } from "../AppModalTypes";

import * as React from "react";

import useInitializeSubscriptionUpdateMutation from "@editor/hooks/trpc/useInitializeSubscriptionUpdateMutation";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { useModal } from "@editor/hooks/useModal";
import { billingRouter } from "@editor/trpc/billing";
import { subscriptionRouter } from "@editor/trpc/subscription";

import {
  getCountOfAdditionalPublishedElementBundlesInSubscription,
  getCountOfV1AdditionalShopsInSubscription,
  getPlanColorTheme,
} from "@/features/workspace/v1/v1BillingUtils";
import {
  V1PlanPricePerMonthBreakdownByProducts,
  V1PlanPricePerMonthIncludingAllProducts,
} from "@/features/workspace/v1/V1PlanPrice";
import { getCountOfV2AdditionalShopsInSubscription } from "@/features/workspace/v2/V2BillingUtils";
import { successToast } from "@replo/design-system/components/alert/Toast";
import Button from "@replo/design-system/components/button/Button";
import { Modal } from "@replo/design-system/components/modal/Modal";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@replo/design-system/components/shadcn/core/card";
import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken } from "@tanstack/react-query";
import { ArrowRight, Check, Info } from "lucide-react";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { capitalizeFirstLetter } from "replo-utils/lib/string";
import { getV1BillingPlanInfoByTier } from "schemas/billing";

export const BillingUpgradeDowngradeModal: React.FC<
  BillingUpgradeDowngradeModalProps
> = ({
  workspaceId,
  currentTier,
  newTier,
  type,
  source,
  newBillingInterval,
  newV1AdditionalShops,
  newAdditionalPublishedElementBundles,
}) => {
  const modal = useModal();
  const { initializeSubscriptionUpdate, isLoading } =
    useInitializeSubscriptionUpdateMutation();

  const isUpgradePlanYearly = newBillingInterval === "yearly";

  const currentTierName = capitalizeFirstLetter(currentTier);
  const newTierName = capitalizeFirstLetter(newTier);

  const currentPlanInfo = getV1BillingPlanInfoByTier(currentTier);
  const newPlanInfo = getV1BillingPlanInfoByTier(newTier);

  const projectId = useCurrentProjectId();

  const copy = exhaustiveSwitch({ type })({
    upgrade: {
      header: `Unlock More with ${newTierName}`,
      subheader: `Here's what you get with your new plan`,
      button: "Confirm Upgrade",
      successTitle: "Congratulations!",
      successBody: "You just upgraded your plan. 🥳",
    },
    downgrade: {
      header: `You're Switching to the ${newTierName} Plan`,
      subheader: "Here's what will change:",
      button: "Confirm Downgrade",
      successTitle: "You successfully downgraded your plan.",
      successBody:
        "Your new tier is active and you can re-upgrade any time. 👋",
    },
    "update-configuration": {
      header: `Update Your Plan's Configuration`,
      subheader: "Here's what your updated plan will look like:",
      button: "Confirm Update",
      successTitle: "You successfully updated your plan's configuration.",
      successBody:
        "Your new configuration is active and you can update it any time. 👋",
    },
  });

  const closeModal = () => {
    modal.closeModal({ type: "billingUpgradeDowngradeModal" });
  };

  const { data: activeSubscription } =
    subscriptionRouter.getActiveSubscription.useQuery(
      workspaceId ? { workspaceId, projectId } : skipToken,
    );

  const { data: nextSubscriptionBillingDate } =
    billingRouter.getNextBillingDate.useQuery(
      workspaceId && activeSubscription?.id
        ? {
            workspaceId,
            subscriptionId: activeSubscription.id,
          }
        : skipToken,
    );

  const isCurrentPlanMonthly = activeSubscription
    ? activeSubscription.billingInterval === "monthly"
    : true;

  const currentV1AdditionalShops = activeSubscription
    ? getCountOfV1AdditionalShopsInSubscription(activeSubscription)
    : 0;
  const currentAdditionalPublishedElementBundles = activeSubscription
    ? getCountOfAdditionalPublishedElementBundlesInSubscription(
        activeSubscription,
      )
    : 0;

  // NOTE (Berkley, 2025-05-08): If the user is upgrading from a monthly plan to a yearly plan,
  // the next billing date is a year from the current date.
  const formattedNextBillingDate = (() => {
    if (
      type === "update-configuration" &&
      isCurrentPlanMonthly &&
      isUpgradePlanYearly
    ) {
      const yearFromNow = new Date();
      yearFromNow.setFullYear(yearFromNow.getFullYear() + 1);
      return yearFromNow.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    }

    return nextSubscriptionBillingDate
      ? new Date(nextSubscriptionBillingDate).toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
        })
      : null;
  })();

  const handleSubscriptionUpdate = async () => {
    // NOTE (Cole 2025-10-01): In the case when we are updating a v1 subscription while there is a v2 subscription
    // that has been cancelled but not ended yet, we will immediately end the v2 subscription.
    // The user should be entitled to finish their period on that v2 subscription but they won't be able to.
    // This is just the simplest solution to this edge case right now - if the user complains we will fix it for them.
    const currentV2AdditionalShops =
      activeSubscription && !activeSubscription.endedAt
        ? getCountOfV2AdditionalShopsInSubscription(activeSubscription)
        : 0;

    const v2BillingTier =
      activeSubscription && !activeSubscription.endedAt
        ? activeSubscription.v2BillingTier
        : "free";

    if (!isLoading) {
      const result = await initializeSubscriptionUpdate({
        workspaceId,
        v1BillingTier: newTier,
        v2BillingTier: v2BillingTier,
        billingInterval: newBillingInterval,
        source,
        v1AdditionalShops: newV1AdditionalShops,
        v2AdditionalShops: currentV2AdditionalShops,
        additionalPublishedElementBundles: newAdditionalPublishedElementBundles,
      });

      if (result.success && !result.confirmationRequired) {
        successToast(
          type === "upgrade" ? "Congratulations!" : "Looks good!",
          type === "upgrade"
            ? "You just upgraded your plan. 🥳"
            : "You downgraded your plan. 👍🏻",
        );
        closeModal();
      }
    }
  };

  const footer = (
    <div className="flex flex-row justify-end gap-2">
      {/* NOTE (Berkley, 2025-05-08): If Upgrade/Downgrade is triggered direct from the billing dashboard manage plans section,
      we don't want to direct back to the billing modal, instead we want to close the modal and do nothing else. */}
      <Button
        variant="secondary"
        size="base"
        onClick={() => {
          closeModal();
          if (source !== "direct.billingDashboardManage") {
            modal.openModal({
              type: "billingModal",
              props: { source },
            });
          }
        }}
        disabled={isLoading}
      >
        Cancel
      </Button>
      <Button
        variant={type === "downgrade" ? "danger" : "primary"}
        size="base"
        isLoading={isLoading}
        onClick={() => void handleSubscriptionUpdate()}
      >
        {copy.button}
      </Button>
    </div>
  );

  const renderDowngradeComparison = ({
    billingIntervalOfNewPlan,
    billingIntervalOfCurrentPlan,
    currentV1AdditionalShops,
    currentAdditionalPublishedElementBundles,
    newV1AdditionalShops,
    newAdditionalPublishedElementBundles,
  }: {
    billingIntervalOfNewPlan: BillingInterval;
    billingIntervalOfCurrentPlan: BillingInterval;
    currentV1AdditionalShops: number;
    currentAdditionalPublishedElementBundles: number;
    newV1AdditionalShops: number;
    newAdditionalPublishedElementBundles: number;
  }) => {
    const currentPlanFeatures = currentPlanInfo.features ?? [];
    const newPlanFeatures = newPlanInfo.features ?? [];

    const uniqueCurrentFeatures = currentPlanFeatures.filter(
      (feature) => !newPlanFeatures.includes(feature),
    );

    const uniqueNewFeatures = newPlanFeatures.filter(
      (feature) => !currentPlanFeatures.includes(feature),
    );

    const shouldShowPerProductBreakdown =
      currentV1AdditionalShops > 0 ||
      currentAdditionalPublishedElementBundles > 0 ||
      newV1AdditionalShops > 0 ||
      newAdditionalPublishedElementBundles > 0;

    return (
      <div className="flex flex-col space-y-4">
        <div className="flex flex-row space-x-4">
          <Card className="w-1/2 bg-neutral-soft">
            <CardHeader>
              <CardTitle className="text-left text-xl">
                {billingIntervalOfCurrentPlan === "yearly"
                  ? `${currentTierName} (Billed Annually)`
                  : `${currentTierName} (Billed Monthly)`}
              </CardTitle>
              {shouldShowPerProductBreakdown && (
                <CardDescription>
                  <V1PlanPricePerMonthBreakdownByProducts
                    plan={currentPlanInfo}
                    billingInterval={billingIntervalOfCurrentPlan}
                    additionalShops={currentV1AdditionalShops}
                    additionalPublishedElementBundles={
                      currentAdditionalPublishedElementBundles
                    }
                  />
                </CardDescription>
              )}
            </CardHeader>

            <CardContent className="gap-1">
              <p className="mb-2 text-left font-semibold">
                You'll lose access to
              </p>
              <div className="space-y-3">
                {uniqueCurrentFeatures.map((feature) => (
                  <div key={feature} className="flex items-start">
                    <span className="text-danger mr-2">✕</span>
                    <span className="text-default line-through">{feature}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          <div className="flex items-center justify-center">
            <ArrowRight size={24} />
          </div>
          <Card className="w-1/2 bg-danger-soft">
            <CardHeader>
              <CardTitle className="text-left text-xl">
                {billingIntervalOfNewPlan === "yearly"
                  ? `${newTierName} (Billed Annually)`
                  : `${newTierName} (Billed Monthly)`}
              </CardTitle>
              {shouldShowPerProductBreakdown && (
                <CardDescription>
                  <V1PlanPricePerMonthBreakdownByProducts
                    plan={newPlanInfo}
                    billingInterval={billingIntervalOfNewPlan}
                    additionalShops={newV1AdditionalShops}
                    additionalPublishedElementBundles={
                      newAdditionalPublishedElementBundles
                    }
                  />
                </CardDescription>
              )}
            </CardHeader>
            <CardContent className="gap-1">
              <p className="mb-2 text-left font-semibold">
                You'll keep access to
              </p>
              <div className="space-y-3">
                {uniqueNewFeatures.map((feature) => (
                  <div key={feature} className="flex items-start">
                    <span className="text-default">{feature}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
        <Card className="mt-1 bg-neutral-soft">
          <CardContent>
            <div className="flex items-start gap-4">
              <div className="text-placeholder flex-shrink-0">
                <Info size={20} />
              </div>
              <div className="text-left text-base flex flex-col gap-4">
                We'll adjust your plan immediately and issue a prorated refund.
                Your new plan takes effect now.
                {formattedNextBillingDate &&
                  ` Your next billing cycle is ${formattedNextBillingDate}.`}
                {billingIntervalOfNewPlan === "yearly" && (
                  <span className="font-semibold">
                    You're about to sign up for an annual plan. Please note this
                    plan is not refundable.
                  </span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderConfigurationComparison = () => {
    const hasShopChanges = currentV1AdditionalShops !== newV1AdditionalShops;
    const hasBundleChanges =
      currentAdditionalPublishedElementBundles !==
      newAdditionalPublishedElementBundles;
    const hasBillingIntervalChange =
      activeSubscription?.billingInterval !== newBillingInterval;

    const shopDifference = newV1AdditionalShops - currentV1AdditionalShops;
    const bundleDifference =
      newAdditionalPublishedElementBundles -
      currentAdditionalPublishedElementBundles;

    if (!hasShopChanges && !hasBundleChanges && !hasBillingIntervalChange) {
      return (
        <div className="text-center py-4 text-muted">
          No configuration changes
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <Card className="bg-neutral-soft">
          <CardHeader>
            <CardTitle className="text-left text-2xl">
              <V1PlanPricePerMonthIncludingAllProducts
                plan={newPlanInfo}
                billingInterval={newBillingInterval}
                additionalShops={newV1AdditionalShops}
                additionalPublishedElementBundles={
                  newAdditionalPublishedElementBundles
                }
                variant="header"
              />
            </CardTitle>
            <CardDescription>
              <V1PlanPricePerMonthBreakdownByProducts
                plan={newPlanInfo}
                billingInterval={newBillingInterval}
                additionalShops={newV1AdditionalShops}
                additionalPublishedElementBundles={
                  newAdditionalPublishedElementBundles
                }
              />
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {hasShopChanges && (
                <div className="flex items-center justify-between p-3 bg-surface rounded-lg">
                  <div className="flex flex-col">
                    <div className="font-medium">
                      Additional Shopify integrations
                    </div>
                    <div className="text-base text-muted">
                      {currentV1AdditionalShops} → {newV1AdditionalShops}
                    </div>
                  </div>
                  <div
                    className={twMerge(
                      "flex items-center gap-1 text-base px-2 py-1 rounded border-border border",
                      shopDifference > 0
                        ? "text-success bg-success-soft"
                        : "text-danger bg-danger-soft",
                    )}
                  >
                    {shopDifference > 0 ? "+" : ""}
                    {shopDifference}
                  </div>
                </div>
              )}

              {hasBundleChanges && (
                <div className="flex items-center justify-between p-3 bg-surface rounded-lg">
                  <div>
                    <span className="font-medium">
                      Additional published item bundles
                    </span>
                    <div className="text-base text-muted">
                      {currentAdditionalPublishedElementBundles} →{" "}
                      {newAdditionalPublishedElementBundles}
                    </div>
                  </div>
                  <div
                    className={twMerge(
                      "flex items-center gap-1 text-base px-2 py-1 rounded border-border border",
                      bundleDifference > 0
                        ? "text-success bg-success-soft"
                        : "text-danger bg-danger-soft",
                    )}
                  >
                    {bundleDifference > 0 ? "+" : ""}
                    {bundleDifference}
                  </div>
                </div>
              )}

              {hasBillingIntervalChange && (
                <div className="flex items-center justify-between p-3 bg-surface rounded-lg">
                  <div>
                    <span className="font-medium">Billing frequency</span>
                    <div className="text-base text-muted">Monthly → Annual</div>
                  </div>
                  <div className="flex items-center gap-1 text-base px-2 py-1 rounded border-border border text-success bg-success-soft">
                    Save 17%
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-neutral-soft">
          <CardContent>
            <div className="flex gap-4 items-start">
              <div className="text-placeholder flex-shrink-0">
                <Info size={20} />
              </div>
              <div className="text-left text-base flex flex-col gap-4">
                We'll adjust your plan immediately and prorate any changes for
                the rest of this cycle. Your new configuration takes effect now.
                {formattedNextBillingDate &&
                  ` Your next billing cycle is ${formattedNextBillingDate}.`}
                {hasBillingIntervalChange &&
                  newBillingInterval === "yearly" && (
                    <span className="font-semibold">
                      You're switching to an annual plan. Please note this plan
                      is not refundable.
                    </span>
                  )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <Modal
      isOpen={true}
      title={copy.header}
      onOpenChange={(open) => {
        if (!open) {
          closeModal();
        }
      }}
      size="lg"
      data-testid="billing-plan-confirmation-modal"
      footer={footer}
    >
      <div className="flex flex-col gap-4 -mt-4">
        {type === "upgrade" && (
          <div className="min-h-[200px] flex flex-col gap-4">
            <div>{copy.subheader}</div>
            <PlanInfoDetails
              plan={newPlanInfo}
              billingInterval={newBillingInterval}
              additionalShops={newV1AdditionalShops}
              additionalPublishedElementBundles={
                newAdditionalPublishedElementBundles
              }
            />
            <Card className="bg-neutral-soft">
              <CardContent>
                <div className="flex items-start gap-4">
                  <div className="text-placeholder flex-shrink-0">
                    <Info size={20} />
                  </div>
                  <div className="text-left text-base flex flex-col gap-4">
                    <span>
                      You'll be charged{" "}
                      <V1PlanPricePerMonthIncludingAllProducts
                        plan={newPlanInfo}
                        billingInterval={newBillingInterval}
                        additionalShops={newV1AdditionalShops}
                        additionalPublishedElementBundles={
                          newAdditionalPublishedElementBundles
                        }
                        variant="base"
                      />{" "}
                      (billed {isUpgradePlanYearly ? "annually" : "monthly"})
                      starting today.
                      {formattedNextBillingDate
                        ? ` Your next billing cycle is ${formattedNextBillingDate}. `
                        : " "}
                      We'll prorate the rest of this cycle.
                    </span>
                    {newBillingInterval === "yearly" && (
                      <span className="font-semibold">
                        You're about to sign up for an annual plan. Please note
                        this plan is not refundable.
                      </span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {type === "update-configuration" && (
          <div className="min-h-[200px] flex flex-col gap-4">
            <div>{copy.subheader}</div>
            {renderConfigurationComparison()}
          </div>
        )}

        {type === "downgrade" && (
          <div className="min-h-[400px] flex flex-col gap-4">
            <div>{copy.subheader}</div>
            {renderDowngradeComparison({
              billingIntervalOfNewPlan: newBillingInterval,
              billingIntervalOfCurrentPlan:
                activeSubscription?.billingInterval ?? "monthly",
              currentV1AdditionalShops,
              currentAdditionalPublishedElementBundles,
              newV1AdditionalShops,
              newAdditionalPublishedElementBundles,
            })}
          </div>
        )}
      </div>
    </Modal>
  );
};

const PlanInfoDetails: React.FC<{
  plan: V1PlanInfo;
  billingInterval: BillingInterval;
  additionalShops: number;
  additionalPublishedElementBundles: number;
}> = ({
  plan,
  billingInterval,
  additionalShops,
  additionalPublishedElementBundles,
}) => {
  const colors = getPlanColorTheme(plan.tier);
  const isYearly = billingInterval === "yearly";

  return (
    <Card
      className={twMerge("border h-full", colors.cardBorder, colors.cardBg)}
    >
      <CardHeader>
        <CardTitle className="text-3xl font-semibold">
          <V1PlanPricePerMonthIncludingAllProducts
            plan={plan}
            billingInterval={billingInterval}
            additionalShops={additionalShops}
            additionalPublishedElementBundles={
              additionalPublishedElementBundles
            }
            variant="header"
          />
        </CardTitle>
        <CardDescription className="text-base text-muted flex flex-col gap-2">
          <div>
            Billed {isYearly ? "annually" : "monthly"}
            {isYearly && (
              <span
                className={twMerge(
                  "text-xs px-1.5 py-0.5 rounded",
                  colors.bg,
                  colors.text,
                )}
              >
                Save 17%
              </span>
            )}
          </div>
          <V1PlanPricePerMonthBreakdownByProducts
            plan={plan}
            billingInterval={billingInterval}
            additionalShops={additionalShops}
            additionalPublishedElementBundles={
              additionalPublishedElementBundles
            }
          />
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {plan.features.map((feature) => (
            <div key={`feature-${feature}`} className="flex items-center gap-2">
              <div className="w-5">
                <Check size={20} className={colors.text} />
              </div>
              <span className="text-base">{feature}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
